import { Arrays, assert, ByteArrayInput, isDefined, Option, panic, Terminable } from "@opendaw/lib-std";
import { Address } from "@opendaw/lib-box";
import { AnimationFrame } from "@opendaw/lib-dom";
import { Communicator } from "@opendaw/lib-runtime";
import { PackageType } from "./PackageType";
import { Subscribers } from "./Subscribers";
import { Lock } from "./Lock";
import { Flags } from "./Flags";
class FloatPackage {
    subscribers = new Subscribers();
    dispatch(address, input) {
        const value = input.readFloat();
        this.subscribers.getOrNull(address)?.forEach(procedure => procedure(value));
    }
    subscribe(address, procedure) {
        return this.subscribers.subscribe(address, procedure);
    }
    hasSubscribers(address) { return !this.subscribers.isEmpty(address); }
    terminate() { this.subscribers.terminate(); }
}
class IntegerPackage {
    subscribers = new Subscribers();
    dispatch(address, input) {
        const value = input.readInt();
        this.subscribers.getOrNull(address)?.forEach(procedure => procedure(value));
    }
    subscribe(address, procedure) {
        return this.subscribers.subscribe(address, procedure);
    }
    hasSubscribers(address) { return !this.subscribers.isEmpty(address); }
    terminate() { this.subscribers.terminate(); }
}
class ArrayPackage {
    subscribers = new Subscribers();
    #arrays = Address.newSet(entry => entry.address);
    dispatch(address, input) {
        const length = input.readInt();
        const entry = this.#arrays.getOrNull(address);
        let array;
        if (isDefined(entry)) {
            array = entry.array;
        }
        else {
            array = this.create(length);
            this.#arrays.add({ address, array });
        }
        this.read(input, array, length);
        this.subscribers.getOrNull(address)?.forEach(procedure => procedure(array));
    }
    subscribe(address, procedure) {
        const subscription = this.subscribers.subscribe(address, procedure);
        return {
            terminate: () => {
                subscription.terminate();
                if (this.subscribers.isEmpty(address) && this.#arrays.hasKey(address)) {
                    this.#arrays.removeByKey(address);
                }
            }
        };
    }
    hasSubscribers(address) { return !this.subscribers.isEmpty(address); }
    terminate() { this.subscribers.terminate(); }
}
class FloatArrayPackage extends ArrayPackage {
    create(length) { return new Float32Array(length); }
    read(input, array, length) {
        for (let i = 0; i < length; i++) {
            array[i] = input.readFloat();
        }
    }
}
class IntegerArrayPackage extends ArrayPackage {
    create(length) { return new Int32Array(length); }
    read(input, array, length) {
        for (let i = 0; i < length; i++) {
            array[i] = input.readInt();
        }
    }
}
class ByteArrayPackage extends ArrayPackage {
    create(length) { return new Int8Array(length); }
    read(input, array, _length) {
        input.readBytes(array);
    }
}
export class LiveStreamReceiver {
    static ID = 0 | 0;
    #float = new FloatPackage();
    #integer = new IntegerPackage();
    #floats = new FloatArrayPackage();
    #integers = new IntegerArrayPackage();
    #bytes = new ByteArrayPackage();
    #packages = [];
    #procedures = [];
    #structure = [];
    #id;
    #optLock = Option.None;
    #sab = Option.None;
    #memory = Option.None;
    #subscriptionFlags = Option.None;
    #numPackages = 0;
    #sabNumPackages = 0;
    #structureVersion = -1;
    #connected = false;
    constructor() {
        this.#id = LiveStreamReceiver.ID++;
        this.#packages[PackageType.Float] = this.#float;
        this.#packages[PackageType.FloatArray] = this.#floats;
        this.#packages[PackageType.Integer] = this.#integer;
        this.#packages[PackageType.IntegerArray] = this.#integers;
        this.#packages[PackageType.ByteArray] = this.#bytes;
    }
    connect(messenger) {
        assert(!this.#connected, "Already connected");
        this.#connected = true;
        return Terminable.many({ terminate: () => { this.#disconnect(); } }, Communicator.executor(messenger, {
            sendShareLock: (lock) => this.#optLock = Option.wrap(new Int8Array(lock)),
            sendUpdateData: (data) => {
                // SAB layout: [subscription flags (numPackages bytes)][data]
                // Store the raw SAB and the numPackages it was created for
                this.#sab = Option.wrap(data);
                this.#sabNumPackages = this.#numPackages;
                this.#updateDataViews();
            },
            sendUpdateStructure: (structure) => {
                this.#updateStructure(new ByteArrayInput(structure));
                this.#updateDataViews();
            }
        }), AnimationFrame.add(() => this.#dispatch()));
    }
    dispatch() { this.#dispatch(); }
    #updateDataViews() {
        if (this.#sab.isEmpty() || this.#numPackages === 0 || this.#numPackages !== this.#sabNumPackages) {
            return;
        }
        const data = this.#sab.unwrap();
        this.#subscriptionFlags = Option.wrap(new Uint8Array(data, 0, this.#numPackages));
        this.#memory = Option.wrap(new ByteArrayInput(data, this.#numPackages));
    }
    #disconnect() {
        this.#memory = Option.None;
        this.#optLock = Option.None;
        this.#sab = Option.None;
        this.#subscriptionFlags = Option.None;
        this.#numPackages = 0;
        this.#sabNumPackages = 0;
        this.#structureVersion = -1;
        this.#connected = false;
        Arrays.clear(this.#procedures);
        Arrays.clear(this.#structure);
        this.#float.terminate();
        this.#floats.terminate();
        this.#integer.terminate();
        this.#integers.terminate();
        this.#bytes.terminate();
    }
    subscribeFloat(address, procedure) {
        return this.#float.subscribe(address, procedure);
    }
    subscribeInteger(address, procedure) {
        return this.#integer.subscribe(address, procedure);
    }
    subscribeFloats(address, procedure) {
        return this.#floats.subscribe(address, procedure);
    }
    subscribeIntegers(address, procedure) {
        return this.#integers.subscribe(address, procedure);
    }
    subscribeByteArray(address, procedure) {
        return this.#bytes.subscribe(address, procedure);
    }
    terminate() { this.#disconnect(); }
    #dispatch() {
        if (this.#optLock.isEmpty() || this.#memory.isEmpty()) {
            return;
        }
        const lock = this.#optLock.unwrap();
        if (Atomics.load(lock, 0) === Lock.READ) {
            const byteArrayInput = this.#memory.unwrap();
            this.#dispatchData(byteArrayInput);
            byteArrayInput.position = 0; // Reset to start of data view (view is already offset past flags)
            this.#writeSubscriptionFlags();
            Atomics.store(lock, 0, Lock.WRITE);
        }
    }
    #writeSubscriptionFlags() {
        if (this.#subscriptionFlags.isEmpty()) {
            return;
        }
        const flags = this.#subscriptionFlags.unwrap();
        for (let i = 0; i < this.#structure.length; i++) {
            const { address, package: pkg } = this.#structure[i];
            flags[i] = pkg.hasSubscribers(address) ? 1 : 0;
        }
    }
    #updateStructure(input) {
        Arrays.clear(this.#procedures);
        Arrays.clear(this.#structure);
        this.#parseStructure(input);
    }
    #dispatchData(input) {
        let version = input.readInt();
        if (version !== this.#structureVersion) {
            // we simply skip and await the latest version soon enough
            return false;
        }
        if (input.readInt() !== Flags.START) {
            throw new Error("stream is broken (no start flag)");
        }
        for (const procedure of this.#procedures) {
            procedure(input);
        }
        if (input.readInt() !== Flags.END) {
            throw new Error("stream is broken (no end flag)");
        }
        return true;
    }
    #parseStructure(input) {
        if (input.readInt() !== Flags.ID) {
            throw new Error("no valid id");
        }
        const version = input.readInt();
        if (version <= this.#structureVersion) {
            return panic("Invalid version. new: " + version + `, was: ${this.#structureVersion}, id: ${this.#id}`);
        }
        this.#structureVersion = version;
        this.#numPackages = input.readInt();
        for (let i = 0; i < this.#numPackages; i++) {
            const address = Address.read(input);
            const pkg = this.#packages[input.readByte()];
            this.#structure.push({ address, package: pkg });
            this.#procedures.push(input => pkg.dispatch(address, input));
        }
    }
}
